import { compose } from "redux";
import { connect } from "react-redux";
import { Grid } from "@material-ui/core";
import Delete from "@material-ui/icons/Delete";
import React, { useEffect, useMemo } from "react";
import { formValueSelector } from "redux-form/immutable";

import {
  ACTIONS,
  SORT_DIRECTION,
  EDIT_FIXED_FACILITY_FORM,
  CREATE_FIXED_FACILITY_FORM,
} from "features/planner/planner.constants";

import {
  editFixedFacilityOnSubmit,
  createFixedFacilityOnSubmit,
  initializeEditFixedFacility,
  getStationaryAndMobileFacilities,
  deleteFixedFacility,
  getFields,
} from "features/planner/planner.actions";

import {
  getSummarizedDataStatesFromState,
  getCurrentUserAppRolesFromState,
} from "app/app.selectors";

import CrudBasePage from "app/components/CrudBasePage";
import { hasRequiredAccessLevel } from "utils/app.util";
import MuiOilrigIcon from "app/components/MuiOilrigIcon";
import { getAccessControlButton } from "utils/component.util";
import { facilityStatusToString } from "features/planner/planner.mappers";
import { getStationaryFacilitiesFromState } from "features/planner/planner.selectors";
import FixedFacilityForm from "features/planner/settings/facilities/components/FixedFacilityForm";
import { getModifiedByValue } from "utils/app.util";

const editFixedFacilitySelector = formValueSelector(
  EDIT_FIXED_FACILITY_FORM.ID,
);

const SettingsFacilitiesContainer = ({
  dataState,
  sourceUri,
  facilities,
  breadcrumb,
  crudAccessLevel,
  dispatchGetFields,
  currentUserAppRoles,
  dispatchGetFixedFacilities,
  dispatchDeleteFixedFacility,
  dispatchEditFixedFacilityOnSubmit,
  dispatchInitializeEditFixedFacility,
  dispatchCreateFixedFacilityOnSubmit,
}) => {
  useEffect(() => {
    dispatchGetFixedFacilities();
    dispatchGetFields();
  }, [dispatchGetFixedFacilities, dispatchGetFields]);

  const columns = useMemo(
    () => [
      {
        xs: true,
        title: "Name",
        getSortProperty: item => item.get("name"),
      },
      {
        xs: true,
        title: "Field",
        getSortProperty: item => item.get("fieldName"),
      },
      {
        xs: true,
        title: "Operator",
        getSortProperty: item => item.getIn(["operator"]),
      },
      {
        xs: true,
        title: "Status",
        getSortProperty: item => facilityStatusToString(item.get("status")),
      },
      {
        xs: true,
        component: Grid,
        title: "Modified by",
        getSortProperty: item => item.get("modifiedBy"),
        getValue: getModifiedByValue,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      getAccessControlButton(
        crudAccessLevel.delete,
        dispatchDeleteFixedFacility,
        <Delete fontSize="small" />,
        "Delete",
      ),
    ],
    [crudAccessLevel, dispatchDeleteFixedFacility],
  );

  return (
    <CrudBasePage
      Icon={MuiOilrigIcon}
      title={breadcrumb}
      items={facilities}
      displayAddButton={hasRequiredAccessLevel(
        crudAccessLevel.create,
        currentUserAppRoles,
      )}
      displaySearchField
      dataState={dataState}
      EditEntityFormComponent={FixedFacilityForm}
      CreateEntityFormComponent={FixedFacilityForm}
      SortableListProps={{
        defaultSortColumn: 0,
        actions: actions,
        columns: columns,
        getKey: facility => facility.get("id"),
        defaultSortDirection: SORT_DIRECTION.ASC,
        onRowClick: hasRequiredAccessLevel(
          crudAccessLevel.edit,
          currentUserAppRoles,
        )
          ? dispatchInitializeEditFixedFacility
          : null,
      }}
      EditModalProps={{
        submitText: "Save",
        title: "Edit facility",
        formTitleField: EDIT_FIXED_FACILITY_FORM.NAME,
        onSubmit: dispatchEditFixedFacilityOnSubmit,
        displaySubmitBtn: sourceUri ? false : true,
      }}
      CreateModalProps={{
        submitText: "Create",
        title: "Create facility",
        onSubmit: dispatchCreateFixedFacilityOnSubmit,
      }}
      editEntityForm={{
        form: EDIT_FIXED_FACILITY_FORM.ID,
      }}
      createEntityForm={{
        form: CREATE_FIXED_FACILITY_FORM.ID,
      }}
    />
  );
};

export default compose(
  connect(
    state => ({
      facilities: getStationaryFacilitiesFromState(state),
      currentUserAppRoles: getCurrentUserAppRolesFromState(state),
      dataState: getSummarizedDataStatesFromState(
        state,
        ACTIONS.GET_STATIONARY_FACILITIES,
        ACTIONS.DELETE_FIXED_FACILITY,
      ),
      sourceUri: editFixedFacilitySelector(
        state,
        EDIT_FIXED_FACILITY_FORM.SOURCE_URI,
      ),
    }),
    {
      dispatchGetFields: getFields,
      dispatchGetFixedFacilities: getStationaryAndMobileFacilities,
      dispatchEditFixedFacilityOnSubmit: editFixedFacilityOnSubmit,
      dispatchDeleteFixedFacility: deleteFixedFacility,
      dispatchInitializeEditFixedFacility: initializeEditFixedFacility,
      dispatchCreateFixedFacilityOnSubmit: createFixedFacilityOnSubmit,
    },
  ),
)(SettingsFacilitiesContainer);
